import { action, computed, observable } from 'mobx'
import { func, type IValidationSchema, validationContext } from 'validx'
import type {
  ListMutator,
  ListOperator,
} from '@/features/legends/effects/effects'
import {
  MutatorEditorViewModelBase,
  type MutatorEditorViewModelOptions,
} from '@/features/legends/effects/MutatorEditorViewModelBase'
import type { EntityDescriptor } from '@/features/legends/editor/entities'
import { type RynoType, Types } from '@/features/legends/editor/ryno-types'
import { RynoEditorViewModel } from '@/features/legends/ryno/RynoEditorViewModel'
import type { SymbolConnectorViewModel } from '@/features/legends/symbols/SymbolConnectorViewModel'

/**
 * Adds items to, or removes them from, list variables.
 */
export class ListMutatorEditorViewModel extends MutatorEditorViewModelBase<ListMutator> {
  private readonly _schema: IValidationSchema<ListMutatorEditorViewModel> = {
    ...this.targetsSchema,
    expr: [func(() => this.expr.analyze(), 'There are formula errors.')],
  }

  readonly type = 'List'

  /**
   * The value, or values, to add or remove.
   */
  readonly expr: RynoEditorViewModel

  @observable accessor op: ListOperator = 'Add'
  @observable accessor multi = false

  readonly validation = validationContext(this, this._schema)

  constructor(options: MutatorEditorViewModelOptions) {
    super(options, 'Please select the variable(s) you wish to change.')
    this.expr = new RynoEditorViewModel({
      required: true,
      getSymbolTable: () => this.symbolTable,
      getNeededType: () => {
        const listType = this.targets.valueType
        if (this.multi) {
          return listType
        }
        return Types.isListType(listType)
          ? listType.elementType
          : Types.list(Types.nothing)
      },
    })
    this.lifecycle.manage(this.expr)
  }

  /**
   * Formulas can use `Value`, the list's current value.
   */
  @computed
  get symbolTable() {
    return this._symbolTableWithValue(this.targets.valueType)
  }

  /**
   * Sets whether the values are added or removed.
   */
  @action.bound
  setOperator(op: ListOperator) {
    this.op = op
  }

  /**
   * Sets whether the formula is one value or a list of them.
   */
  @action.bound
  setMulti(multi: boolean) {
    this.multi = multi
  }

  /** @inheritDoc */
  @action
  hydrate(mutator: ListMutator) {
    super.hydrate(mutator)
    this.expr.setValue(mutator.expr)
    if (mutator.op) {
      this.op = mutator.op
    }
    this.multi = !!mutator.multi
  }

  /** @inheritDoc */
  toJSON(): ListMutator {
    return {
      type: this.type,
      targets: this.targets.toJSON(),
      op: this.op,
      expr: this.expr.toJSON(),
      multi: this.multi,
    }
  }

  /**
   * Only lists, and once there is more than one target, only lists of the
   * first target's type.
   */
  protected _isCompatibleTarget(
    { type }: EntityDescriptor,
    valueType: RynoType | null,
    connector: SymbolConnectorViewModel,
  ) {
    if (type !== 'Variable' || !Types.isListType(valueType)) {
      return false
    }
    return this._matchesFirstTarget(valueType, connector)
  }
}
